/* eslint-env browser */
// Reports › Earning reports (wave 3, W2_Portal_Tables look): the platform's
// earnings per month in the modern table, with the totals of the months
// listed as figure tiles above it.
//
// Every figure keeps its formula: Gross trip cost = trip cost + cancellation
// fee; Discounts = discountAmount; Profit (Revenue) = convenience fee +
// cancellation fee − discounts (each shown to settings.decimal places). The
// tiles add the same per-month figures up. Data, exactly as before: the
// report (fetchEarningsReport) is asked for once per mount, only when the
// slice is empty and not loading (deferred from AuthLoading for RTDB cost).
// Export (CSV and print) for admins only, as before; the computed columns
// are now in the CSV too.
import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { api } from 'common';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import QueryStatsOutlinedIcon from '@mui/icons-material/QueryStatsOutlined';
import DashboardCard from '../components/DashboardCard';
import { DataTable, calmCaps, formatMoney, formatNumber } from '../components/modern';

// The feed reports an empty result as a failure with this text.
const NO_DATA_MSG = 'No data available.';
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const NUMBER_FORMAT = new Intl.NumberFormat('en-US');
const fmt = (n) => NUMBER_FORMAT.format(n || 0);

// Month names in the portal's language, falling back to the English name.
const monthName = (lang, monthIndex, fallback) => {
  const m = Number(monthIndex);
  if (monthIndex === undefined || monthIndex === null || !Number.isInteger(m) || m < 0 || m > 11) return fallback || '';
  try {
    return new Intl.DateTimeFormat(lang || 'en', { month: 'long', timeZone: 'UTC' }).format(new Date(Date.UTC(2000, m, 15)));
  } catch (e) {
    return fallback || '';
  }
};
const monthIndexOf = (row) => {
  const m = Number(row.month);
  if (row.month !== undefined && row.month !== null && Number.isInteger(m)) return m;
  const i = MONTHS.indexOf(row.monthsName);
  return i >= 0 ? i : 0;
};

export default function Earningreports() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const settings = useSelector((state) => state.settingsdata.settings);
  const auth = useSelector((state) => state.auth);

  // Helper function to safely parse float values
  const safeParseFloat = (value) => {
    const parsed = parseFloat(value);
    return isNaN(parsed) ? 0 : parsed;
  };
  const gross = (row) => (safeParseFloat(row.tripCost) + safeParseFloat(row.cancellationFee)).toFixed(settings.decimal);
  const discounts = (row) => safeParseFloat(row.discountAmount).toFixed(settings.decimal);
  const profit = (row) => (safeParseFloat(row.convenienceFee) + safeParseFloat(row.cancellationFee) - safeParseFloat(row.discountAmount)).toFixed(settings.decimal);

  const columns = [
    { id: 'year', title: t('year'), field: 'year' },
    {
      id: 'month',
      title: t('months'),
      field: 'monthsName',
      render: (row) => monthName(lang, row.month, row.monthsName),
      searchValue: (row) => monthName(lang, row.month, row.monthsName),
      exportValue: (row) => monthName(lang, row.month, row.monthsName),
      sortValue: (row) => Number(row.year) * 100 + monthIndexOf(row),
    },
    { id: 'total_rides', title: t('booking_count'), field: 'total_rides', type: 'number' },
    {
      id: 'gross',
      title: t('Gross_trip_cost'),
      type: 'money',
      sortValue: (row) => Number(gross(row)),
      searchValue: gross,
      exportValue: gross,
      render: (row) => formatMoney(gross(row), settings),
    },
    { id: 'rideCost', title: t('trip_cost_driver_share'), field: 'rideCost', type: 'money' },
    { id: 'cancellationFee', title: t('cancellationFee'), field: 'cancellationFee', type: 'money' },
    { id: 'convenienceFee', title: t('convenience_fee'), field: 'convenienceFee', type: 'money' },
    { id: 'fleetadminFee', title: t('fleet_admin_fee'), field: 'fleetadminFee', type: 'money' },
    {
      id: 'discounts',
      title: t('Discounts'),
      type: 'money',
      sortValue: (row) => Number(discounts(row)),
      searchValue: discounts,
      exportValue: discounts,
      render: (row) => formatMoney(discounts(row), settings),
    },
    {
      id: 'profit',
      title: t('Profit'),
      type: 'money',
      sortValue: (row) => Number(profit(row)),
      searchValue: profit,
      exportValue: profit,
      render: (row) => (
        <Box component="span" sx={{ fontWeight: 700 }}>
          {formatMoney(profit(row), settings)}
        </Box>
      ),
    },
  ];

  const [data, setData] = useState([]);
  const earningreportsdata = useSelector((state) => state.earningreportsdata);
  const dispatch = useDispatch();
  const { fetchEarningsReport } = api;
  // Deferred from AuthLoading (RTDB cost): fetch once per mount, and only when
  // the slice is empty and not already loading, so tab-switch remounts never
  // re-run the heavy legacy read.
  const requestedRef = useRef(false);
  useEffect(() => {
    if (requestedRef.current) return;
    requestedRef.current = true;
    if (!earningreportsdata.Earningreportss && !earningreportsdata.loading && !(earningreportsdata.error && earningreportsdata.error.flag)) {
      dispatch(fetchEarningsReport());
    }
  }, [dispatch, fetchEarningsReport, earningreportsdata.Earningreportss, earningreportsdata.loading, earningreportsdata.error]);

  useEffect(() => {
    if (earningreportsdata.Earningreportss) {
      setData(earningreportsdata.Earningreportss);
    }
  }, [earningreportsdata.Earningreportss]);

  const loading = !!earningreportsdata.loading;
  const error = earningreportsdata.error || {};
  const failed = !loading && !!error.flag && error.msg !== NO_DATA_MSG && !data.length;
  const isAdmin = auth.profile && auth.profile.usertype === 'admin';
  const title = calmCaps(t('earning_reports_title', { defaultValue: 'Earning reports' }));

  // Totals of the months listed (the same per-month figures, added up).
  const totals = data.reduce(
    (acc, row) => ({
      rides: acc.rides + safeParseFloat(row.total_rides),
      gross: acc.gross + Number(gross(row)),
      profit: acc.profit + Number(profit(row)),
    }),
    { rides: 0, gross: 0, profit: 0 }
  );
  const caption = t('earnings_totals_caption', {
    count: data.length,
    n: fmt(data.length),
    defaultValue_one: 'Total of {{n}} month',
    defaultValue_other: 'Total of {{n}} months',
    defaultValue: 'Total of {{n}} months',
  });
  const tilesLoading = loading && !data.length;

  return (
    <Box dir={i18n.dir()} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {data.length || tilesLoading ? (
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(3, minmax(0, 1fr))' } }}>
          <DashboardCard icon={<ConfirmationNumberOutlinedIcon />} hue="info" title={t('booking_count')} value={formatNumber(totals.rides)} caption={caption} loading={tilesLoading} />
          <DashboardCard icon={<PaymentsOutlinedIcon />} hue="accent" title={t('Gross_trip_cost')} value={formatMoney(totals.gross, settings, true)} caption={caption} loading={tilesLoading} />
          <DashboardCard icon={<TrendingUpRoundedIcon />} hue="success" title={t('Profit')} value={formatMoney(totals.profit, settings, true)} caption={caption} loading={tilesLoading} />
        </Box>
      ) : null}
      <DataTable
        title={title}
        rows={data}
        columns={columns}
        rowKey={(row, index) => row.uniqueKey || `${row.year}-${row.monthsName}-${index}`}
        loading={tilesLoading}
        refreshing={loading && data.length > 0}
        error={failed ? { title: t('earning_reports_load_failed', { defaultValue: 'Could not load the earning reports.' }) } : null}
        countText={({ shown, total, query, loading: busy }) =>
          busy
            ? t('loading', { defaultValue: 'Loading…' })
            : query
              ? t('table_count_shown', { shown: fmt(shown), total: fmt(total), defaultValue: '{{shown}} of {{total}}' })
              : t('earnings_months_count', {
                  count: total,
                  n: fmt(total),
                  defaultValue_one: '{{n}} month',
                  defaultValue_other: '{{n}} months',
                  defaultValue: '{{n}} months',
                })
        }
        prefsKey="earning_reports"
        exportCsv={isAdmin}
        exportPrint={isAdmin}
        exportFileName="earning_reports"
        exportTitle={title}
        minWidth={1100}
        emptyState={({ query }) =>
          query || failed
            ? null
            : {
                icon: <QueryStatsOutlinedIcon />,
                title: t('earning_reports_empty', { defaultValue: 'No earnings yet' }),
                body: t('earnings_empty_body', { defaultValue: 'Each month shows here once drivers have completed and paid trips.' }),
              }
        }
      />
    </Box>
  );
}
